import React from 'react';
import { CopilotAutoReschedulingNotificationsCallout } from 'owa-auto-rescheduling-notification-callout';
import type { OwaHeaderProps } from 'owa-bootstrap';
import { getBposNavBarData } from 'owa-bpos-store';
import { isCapabilityEnabled, isCapabilitySupported } from 'owa-capabilities';
import { customNotificationSoundCapability } from 'owa-capabilities-definitions/lib/customNotificationSoundCapability';
import { novaMeControlCapability } from 'owa-capabilities-definitions/lib/novaMeControlCapability';
import type { MailboxInfo } from 'owa-client-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { OWA_TITLE_BAR_ID } from 'owa-header-constants';
import { MeControlLauncher } from 'owa-mecontrol-launcher';
import { LazyNotificationSoundCustomizationTeachingCallout } from 'owa-notification-sound-customization-teaching-callout';
import { MergedNotificationPane } from 'owa-header-charms';
import { PendingAccountsWelcomeCallout as LazyPendingAccountsWelcomeCallout } from 'owa-mail-accounts-view-callout';
import { observer } from 'owa-mobx-react';
import { LazyOwaMonarchReferralCampaign } from 'owa-new-outlook-promo-dialog';
import { LazyPstTeachingCallout } from 'owa-pst-teaching-callout';
import {
    RepromptDialog as LazyRepromptDialog,
    RepromptDialogV2 as LazyRepromptDialogV2,
} from 'owa-reprompt-dialog';
import type NavBarData from 'owa-service/lib/contract/NavBarData';
import { LazyAddSharedMailboxesAsAccountTeachingCallout } from 'owa-shared-with-me-teaching-callout';
import { HEADER_RESPONSIVE_BREAKPOINT } from '../constants/constants';
import useHeaderResponsive from '../hooks/useHeaderResponsive';
import getLeftCharmsContainerStyle from '../utils/charms/getLeftCharmsContainerStyle';
import OwaHeaderButtonGroup from './buttons/OwaHeaderButtonGroup';
import OwaHeaderAppIcon from './charms/OwaHeaderAppIcon';
import OwaHeaderAppLauncher from './charms/OwaHeaderAppLauncher';
import OwaHeaderBrandingLabel from './charms/OwaHeaderBrandingLabel';
import OwaHeaderDevSourceLabel from './charms/OwaHeaderDevSourceLabel';
import OwaHeaderTenantLogo from './charms/OwaHeaderTenantLogo';
import OwaHeaderMeControl from './mecontrol/OwaHeaderMeControl';
import OwaShyHeaderWrapper from './shyHeader/OwaShyHeaderWrapper';
import {
    headerRoot,
    headerLeft,
    headerCenter,
    headerCharmsAndSearch,
    headerLeftCharms,
    headerSearchScopePicker,
    headerSearchBox,
} from './OwaHeader.scss';

const defaultExport: React.FunctionComponent<OwaHeaderProps> = observer(function OwaHeader(
    props: OwaHeaderProps
) {
    const navBarData = getBposNavBarData() as NavBarData | null;
    const waffleButtonRef = React.useRef<HTMLButtonElement>(null);

    const charmsAndSearchRef = useHeaderResponsive(HEADER_RESPONSIVE_BREAKPOINT, {
        searchBoxRespond: props.searchBoxRespond,
        currentModule: props.currentModule,
    });

    const header = (
        <div id={OWA_TITLE_BAR_ID} className={headerRoot}>
            <OwaShyHeaderWrapper waffleButtonRef={waffleButtonRef}>
                <div className={headerLeft}>
                    <OwaHeaderAppIcon />
                    {!props.disableWaffle && (
                        <OwaHeaderAppLauncher buttonRef={waffleButtonRef} navBarData={navBarData} />
                    )}
                </div>
                <div className={headerCenter}>
                    <div className={headerCharmsAndSearch} ref={charmsAndSearchRef}>
                        <div
                            className={headerLeftCharms}
                            style={getLeftCharmsContainerStyle(props.searchAlignmentWidth)}
                        >
                            <OwaHeaderDevSourceLabel />
                            <OwaHeaderTenantLogo navBarData={navBarData} />
                            <OwaHeaderBrandingLabel />
                            <div className={headerSearchScopePicker}>
                                {props.renderSearchScopePicker?.()}
                            </div>
                        </div>
                        <div className={headerSearchBox}>{props.renderSearch?.()}</div>
                        {!props.disableButtonGroup && <OwaHeaderButtonGroup />}
                    </div>
                </div>
                <OwaHeaderMeControl />
            </OwaShyHeaderWrapper>
        </div>
    );

    return (
        <>
            <LazyAddSharedMailboxesAsAccountTeachingCallout />
            {isCapabilitySupported(customNotificationSoundCapability) && (
                <LazyNotificationSoundCustomizationTeachingCallout />
            )}
            {isFeatureEnabled('acct-owaMonarchReferral') && <LazyOwaMonarchReferralCampaign />}
            {isFeatureEnabled('acct-pstFileSupport') && <LazyPstTeachingCallout />}
            {isFeatureEnabled('settings-repromptDialog-v2') ? (
                <LazyRepromptDialogV2 />
            ) : (
                <LazyRepromptDialog />
            )}
            {isFeatureEnabled('time-copilot-managed-events') && (
                <CopilotAutoReschedulingNotificationsCallout />
            )}
            <LazyPendingAccountsWelcomeCallout />
            <MergedNotificationPane key="notificationPane" />
            {getWrappedHeader(header, props)}
        </>
    );
},
'OwaHeader');
export default defaultExport;

function getWrappedHeader(header: React.ReactElement, props: OwaHeaderProps): React.ReactNode {
    // Conditionally wrap the header with the MeControl container or custom wrapper
    if (props.wrapSuiteHeader) {
        const novaMeControl = isCapabilityEnabled(novaMeControlCapability)
            ? (MeControlLauncher as React.FC<{
                  mailboxInfo: MailboxInfo;
              }>)
            : undefined;

        return props.wrapSuiteHeader(header, novaMeControl);
    } else {
        return header;
    }
}
